import { useMemo, useState } from "react"
import { IconClipboardCheck, IconLoader2, IconRefresh } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import {
  useGetBookingQuery,
  useGetCalUsersQuery,
  useListBookingsNeedingUpdateQuery,
} from "@/app/booking-calendar/bookingCalendarApi"
import type { BookingRequestListItem } from "@/app/booking-calendar/bookingCalendarApi"
import { AppointmentDialog } from "@/app/booking-calendar/components/modals"
import { BookingRequestStatus, statusBadgeClass, statusLabel } from "@/app/booking-calendar/lib/bookingStatus"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"

const PAGE_SIZE = 50

/** The host filter's "everyone" value — a Select item cannot carry an empty string. */
const ALL_HOSTS = "all"

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—"
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

/** "3 days ago", or hours for the same day — how overdue the update is, at a glance. */
function describeAge(value: string | null | undefined, now: number): string {
  if (!value) return "—"
  const hours = Math.max(0, Math.floor((now - new Date(value).getTime()) / 3_600_000))
  if (hours < 24) return hours <= 1 ? "Within the hour" : `${hours} hours ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? "1 day ago" : `${days} days ago`
}

/** A date input's "YYYY-MM-DD" as the start of that day, local time, in ISO form. */
function startOfDay(value: string): string | undefined {
  if (!value) return undefined
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** The day after, so the "to" date includes the whole of the day it names. */
function endOfDay(value: string): string | undefined {
  if (!value) return undefined
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return undefined
  date.setDate(date.getDate() + 1)
  return date.toISOString()
}

/**
 * Appointments that are over and still have no outcome.
 *
 * The host of an appointment is expected to record what happened once it is done — Complete, No
 * show, and so on. This is their to-do list: every appointment that has ended and is still Booked,
 * plus every one a day past that the sweep has marked Incomplete. Staff see the appointments they
 * host; administrators (`appointments:manage`) see everyone's and can narrow to one host.
 *
 * Choosing a row opens the same appointment dialog the calendar uses, where the status is updated.
 * The row drops off this list as soon as it is saved.
 */
export default function IncompleteBookingsPage() {
  const { permissions } = useAppSelector(state => state.permissions)
  const isAdmin = hasPermission(permissions, "appointments:manage")

  const [host, setHost] = useState<string>(ALL_HOSTS)
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<BookingRequestListItem | null>(null)

  const query = useListBookingsNeedingUpdateQuery({
    hostCalUserId: isAdmin && host !== ALL_HOSTS ? Number(host) : undefined,
    from: startOfDay(from),
    to: endOfDay(to),
    take: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  })

  // The organization's Cal users are who a booking can be hosted by, so they are the host filter.
  // Only administrators filter; staff are only ever shown their own.
  const { data: calUsers } = useGetCalUsersQuery(undefined, { skip: !isAdmin })
  const hostOptions = useMemo(
    () => [...(calUsers ?? [])].sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email)),
    [calUsers],
  )

  // The dialog takes Cal's booking, which this list does not carry — it is fetched on selection.
  const selectedBooking = useGetBookingQuery(selected?.calBookingUid ?? "", { skip: !selected?.calBookingUid })

  const rows = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)
  const now = Date.now()

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconClipboardCheck className="size-6" />
          <h1 className="text-2xl font-bold">Incomplete Bookings</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
          {query.isFetching ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
          Refresh
        </Button>
      </div>
      <p className="text-muted-foreground max-w-3xl">
        {isAdmin
          ? "Appointments that have ended without an outcome being recorded, for every host. "
          : "Appointments you hosted that have ended without an outcome being recorded. "}
        Open one to record what happened. A booking still not updated a day after its appointment is
        marked Incomplete.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        {isAdmin && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="host-filter" className="text-xs">Host</Label>
            <Select value={host} onValueChange={value => { setHost(value); setPage(0) }}>
              <SelectTrigger id="host-filter" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_HOSTS}>All hosts</SelectItem>
                {hostOptions.map(user => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {user.name ?? user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="from-filter" className="text-xs">From</Label>
          <Input id="from-filter" type="date" className="w-40" value={from} onChange={event => { setFrom(event.target.value); setPage(0) }} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="to-filter" className="text-xs">To</Label>
          <Input id="to-filter" type="date" className="w-40" value={to} onChange={event => { setTo(event.target.value); setPage(0) }} />
        </div>
        {(from || to || host !== ALL_HOSTS) && (
          <Button variant="ghost" size="sm" onClick={() => { setFrom(""); setTo(""); setHost(ALL_HOSTS); setPage(0) }}>
            Clear filters
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : query.isError ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">The list of incomplete bookings could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>Retry</Button>
        </div>
      ) : query.data?.noCalAccount ? (
        <div className="rounded-xl border bg-card p-6 text-muted-foreground">
          You do not have an account in the scheduling system, so no appointments are hosted by you.
          Ask an administrator to add you if you should have one.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-muted-foreground">
          {isAdmin ? "Every appointment that has ended has an outcome recorded." : "You are up to date: every appointment you hosted has an outcome recorded."}
        </div>
      ) : (
        <>
          <div className="rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Appointment</TableHead>
                  <TableHead>Site</TableHead>
                  {isAdmin && <TableHead>Host</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Ended</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow
                    key={row.id}
                    className={row.calBookingUid ? "cursor-pointer" : undefined}
                    tabIndex={row.calBookingUid ? 0 : undefined}
                    onClick={() => { if (row.calBookingUid) setSelected(row) }}
                    onKeyDown={event => {
                      if (row.calBookingUid && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault()
                        setSelected(row)
                      }
                    }}
                  >
                    <TableCell>
                      <div className="font-medium">{formatDateTime(row.slotStart)}</div>
                      <div className="text-sm text-muted-foreground">{row.attendeeName || "—"}</div>
                    </TableCell>
                    <TableCell>{row.siteName ?? row.calTeamSlug}</TableCell>
                    {isAdmin && <TableCell>{row.hostName ?? <span className="text-muted-foreground">Not yet known</span>}</TableCell>}
                    <TableCell>
                      <Badge variant="outline" className={statusBadgeClass(row.status)}>
                        {row.status === BookingRequestStatus.Booked ? "Booked – ended" : statusLabel(row.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{describeAge(row.slotEnd ?? row.slotStart, now)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}

      {selected && selectedBooking.isError && (
        <div className="flex items-center justify-between gap-2 rounded-xl border bg-card p-4 text-sm">
          <span className="text-destructive">That appointment could not be loaded from the scheduling system.</span>
          <Button variant="outline" size="sm" onClick={() => setSelected(null)}>Dismiss</Button>
        </div>
      )}

      {selected && (
        <AppointmentDialog
          booking={selectedBooking.data}
          staffName={selected.hostName ?? undefined}
          timeZone={SITE_TIME_ZONE}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
